/**
 * Отрезок ленты: один видеофайл со своим набором кадров, лежащим в общем
 * массиве подряд. Нужен, когда лента склеена из нескольких файлов — интервал
 * между кадрами у них может отличаться (кап MAX_FRAMES действует на файл),
 * и один общий `durationMs / frameUrls.length` увёл бы кадры от их времени.
 */
export type FilmstripSegment = {
  /** Начало файла на общей ленте. */
  offsetMs: number
  durationMs: number
  /** Сколько кадров этого файла лежит в общем массиве. */
  frameCount: number
}

/** Глобальный индекс кадра для точки общей ленты. */
export function filmstripIndexAtMs(segments: FilmstripSegment[], ms: number): number {
  let base = 0
  for (const segment of segments) {
    const end = segment.offsetMs + segment.durationMs
    if (segment.frameCount <= 0) continue
    if (ms < end || segment === segments[segments.length - 1]) {
      const local = Math.max(0, ms - segment.offsetMs)
      const interval = segment.durationMs > 0 ? segment.durationMs / segment.frameCount : 1
      const idx = interval > 0 ? Math.floor(local / interval) : 0
      return base + Math.min(Math.max(idx, 0), segment.frameCount - 1)
    }
    base += segment.frameCount
  }
  return Math.max(0, base - 1)
}

/**
 * Evenly sample frame indices across [rangeStartMs, rangeEndMs] for a classic flex filmstrip.
 * Approximate time correspondence: first index ≈ floor(start/interval), last ≈ end of range.
 * Always returns `count` indices: when the range has fewer extracted frames than slots,
 * frames repeat so thumbs keep their natural width instead of stretching across the strip.
 */
export function sampleFilmstripIndices(args: {
  rangeStartMs: number
  rangeEndMs: number
  intervalMs: number
  frameCount: number
  /** How many thumbs to show (from fitting width). */
  count: number
  /** Разбиение ленты по файлам; без него интервал считается общим на всю ленту. */
  segments?: FilmstripSegment[]
}): number[] {
  const intervalMs = Math.max(1, args.intervalMs)
  const frameCount = Math.max(0, Math.floor(args.frameCount))
  const count = Math.max(1, Math.floor(args.count))
  if (frameCount <= 0) return []

  // Лента из нескольких файлов: сэмплируем по ВРЕМЕНИ и переводим каждую точку
  // в индекс через её файл — единый интервал тут неверен.
  const segments = args.segments
  if (segments && segments.length > 1) {
    const span = Math.max(0, args.rangeEndMs - args.rangeStartMs)
    const step = count > 1 ? span / (count - 1) : 0
    const out: number[] = []
    for (let i = 0; i < count; i++) {
      const ms = args.rangeStartMs + i * step
      out.push(Math.min(Math.max(filmstripIndexAtMs(segments, ms), 0), frameCount - 1))
    }
    return out
  }

  const maxIdx = frameCount - 1
  const startFrame = Math.floor(args.rangeStartMs / intervalMs)
  const endFrame = Math.ceil(args.rangeEndMs / intervalMs)
  const spanFrames = Math.max(1, endFrame - startFrame)
  const availableCount = Math.max(1, Math.min(spanFrames, Math.max(0, maxIdx - startFrame + 1)))

  const step = availableCount > 1 ? (availableCount - 1) / (count - 1 || 1) : 0
  const indices: number[] = []
  for (let i = 0; i < count; i++) {
    const idx = startFrame + Math.round(i * step)
    indices.push(Math.min(Math.max(idx, 0), maxIdx))
  }
  return indices
}

/** Кадры исходника для окна [rangeStartMs, rangeEndMs), пересэмплированные под это окно. */
export function filmstripUrlsForRange(
  frameUrls: string[],
  sourceDurationMs: number,
  rangeStartMs: number,
  rangeEndMs: number,
): string[] {
  if (frameUrls.length === 0) return []
  const intervalMs = Math.max(1, sourceDurationMs) / frameUrls.length
  const spanMs = Math.max(1, rangeEndMs - rangeStartMs)
  const count = Math.max(1, Math.round(spanMs / intervalMs))
  const lastIdx = frameUrls.length - 1
  return Array.from({ length: count }, (_, i) => {
    const ms = rangeStartMs + ((i + 0.5) * spanMs) / count
    return frameUrls[Math.min(lastIdx, Math.max(0, Math.floor(ms / intervalMs)))]
  })
}
